import { alpha } from "@mui/material/styles";

/**
 * Colours written as a CSS custom property rather than a hex literal, so they
 * follow whichever colour scheme the theme is rendering.
 *
 * A theme built from plain hex freezes its colours at construction: `color:
 * "#00344F"` is that navy under every scheme. MUI emits its palette as custom
 * properties when a theme sets `cssVariables`, so referencing the property
 * instead defers the value to render time, where the active scheme decides it.
 *
 * The literal moves into the reference's fallback, which is what themes without
 * `cssVariables` render — every consumer of `createSharedTheme` except the
 * composed Kaizen theme. Those keep the exact colour they have today.
 */

/**
 * MUI's `cssVarPrefix`. Owned here rather than in `kaizen-theme.ts` so the
 * theme that emits the properties and the helper that references them cannot
 * disagree about their names.
 */
export const CSS_VAR_PREFIX = "ds-mui";

/**
 * A palette colour as a CSS custom property, falling back to `fallback`.
 *
 * `token` is a palette path with `.` written as `-`: `text-primary`,
 * `primary-main`, `error-main`.
 *
 * Pair a token with the slot holding the same *colour* as the fallback, not the
 * one holding the same name. `primary` and `secondary` name opposite colours in
 * this theme and in design's — see the `brand` note in `shared.tsx` — so
 * `secondary-main` renders a purple link teal. `shared-theme-colors.test.ts`
 * asserts every pairing.
 */
export const paletteVar = (token: string, fallback: string): string =>
  `var(--${CSS_VAR_PREFIX}-palette-${token}, ${fallback})`;

/**
 * A colour at partial opacity, whether or not it is a custom property.
 *
 * `alpha()` from MUI parses hex and throws `Unsupported ... color` on a custom
 * property, so a reference has to do its transparency in CSS instead. A literal
 * still goes through `alpha()`, which keeps `rgba()` output for the themes that
 * emit no properties rather than moving them onto `color-mix` for nothing.
 *
 * `color-mix` needs Chrome 111+, Safari 16.2+, Firefox 113+ — inside the range
 * `kaizen-theme.ts` already requires for the palette's `oklch` values, and only
 * reached by themes that opt into custom properties in the first place.
 */
export const paletteVarAlpha = (colour: string, opacity: number): string =>
  colour.startsWith("var(")
    ? `color-mix(in srgb, ${colour} ${opacity * 100}%, transparent)`
    : alpha(colour, opacity);
